import { Check, Copy, X } from "lucide-react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import tickIcon from "../assets/circular-tick-white-bg.svg";
import { useResizablePanel } from "../hooks/useResizablePanel";
import { type EvidenceEvent, sortBySequence } from "../types/evidence";
import { type ColumnInspection } from "./Chat/Chat.types";
import { renderEvidenceEvent } from "./registry/componentRegistry";
import { useCitationChain } from "./CitationChain/CitationChainContext";
import CitationChainPanel from "./CitationChain/CitationChainPanel";
import { isAuditTimelineEnabled } from "../utils/featureFlags";
import {
	auditLogCounts,
	buildAuditTimeline,
	formatAuditLogCounts,
} from "../utils/auditTimeline";
import AuditTimeline from "./AuditTimeline";
import { ContentBlock, TraceBlock, UIDocumentRef } from "./Chat/Chat.types";
import { renderTraceBlock } from "./registry/traceRegistry";
import ResizeHandle from "./ResizeHandle";
import TraceStepItem from "./TraceStepItem";
import "./TracePanel.css";

const TRACE_PANEL_DEFAULT_WIDTH = 380;
const TRACE_PANEL_MIN_WIDTH = 300;
const TRACE_PANEL_MAX_WIDTH = 640;

interface TracePanelProps {
	blocks: TraceBlock[];
	/**
	 * Every block of the active message, in order. The audit timeline needs
	 * the full sequence — narration is a `text` block, and its position
	 * relative to the tool calls is what makes it narration. Optional so
	 * callers that only have trace blocks keep the legacy verbose view.
	 */
	contentBlocks?: ContentBlock[];
	documentRefs?: UIDocumentRef[];
	/** The chat's arm; only `claude_code` routes narration into the lane. */
	chatType?: string | null;
	events?: EvidenceEvent[];
	/** Session-only column-lineage inspection series for the active message. */
	inspections?: ColumnInspection[];
	chatId?: string | null;
	isStreaming: boolean;
	isOpen: boolean;
	onClose: () => void;
	/**
	 * Where this panel is mounted (DOP-492).
	 *
	 * `drawer` (the default, and the chat route) is the 380px resizable rail
	 * that slides in beside the thread. `embedded` is the SAME log with no
	 * chrome of its own: it fills whatever the host gives it — the Analyst
	 * rail hands it the rail's whole body, in place of the thread — and
	 * heads it with a way back rather than a width to drag.
	 */
	variant?: "drawer" | "embedded";
	/**
	 * The question this log belongs to, one line under the title. A rail that
	 * has replaced the thread with the log has taken the answer off screen,
	 * so the log has to say which answer it is for. Drawer mode ignores it —
	 * the answer is right there beside it.
	 */
	aboutLabel?: string;
}

/**
 * One logged column-lineage inspection, rendered as an audit step. Shows the
 * clicked column, whether it was extracted or derived, its source cell(s), the
 * English transform, and the SQL expression.
 */
const InspectionStepItem: React.FC<{ inspection: ColumnInspection }> = ({
	inspection,
}) => {
	const lineage = inspection.lineage;
	const isExtracted = lineage?.kind === "extracted";
	return (
		<div className="trace-inspection-step">
			<div className="trace-inspection-header">
				<span className="trace-inspection-seq">{inspection.seq}</span>
				<span className="trace-inspection-title">{inspection.header}</span>
				<span
					className={`trace-inspection-kind${
						isExtracted
							? " trace-inspection-kind-extracted"
							: " trace-inspection-kind-derived"
					}`}
				>
					{isExtracted ? "Extracted" : "Derived"}
				</span>
			</div>
			{lineage && lineage.sources.length > 0 && (
				<div className="trace-inspection-sources">
					{lineage.sources.map((s, i) => (
						<div key={i} className="trace-inspection-source">
							{s.tab ? `${s.tab} · ` : ""}
							{s.column || "—"}
							{s.canonical_field ? ` → ${s.canonical_field}` : ""}
						</div>
					))}
				</div>
			)}
			{lineage?.transform && (
				<div className="trace-inspection-transform">{lineage.transform}</div>
			)}
			{lineage?.sql && (
				<pre className="trace-inspection-sql">
					<code>{lineage.sql}</code>
				</pre>
			)}
		</div>
	);
};

const TracePanel: React.FC<TracePanelProps> = ({
	blocks,
	contentBlocks,
	documentRefs,
	chatType,
	events,
	inspections,
	chatId,
	isStreaming,
	isOpen,
	onClose,
	variant = "drawer",
	aboutLabel,
}) => {
	const isEmbedded = variant === "embedded";
	const [copied, setCopied] = useState(false);
	const { width, isResizing, startResize } = useResizablePanel({
		initialWidth: TRACE_PANEL_DEFAULT_WIDTH,
		minWidth: TRACE_PANEL_MIN_WIDTH,
		maxWidth: TRACE_PANEL_MAX_WIDTH,
		edge: "left",
	});
	// The audit timeline replaces the raw tool rows with semantic steps, so
	// it also owns the step count in the header badge — collapsed rows count
	// once, hidden plumbing (ToolSearch) counts zero.
	const useTimeline = isAuditTimelineEnabled() && Boolean(contentBlocks);

	// Not filtered. `web_source` events used to be dropped on the timeline path
	// because the standalone Evidence section restated the "Searched the web"
	// step; that section no longer renders here at all, and the drawer's badge
	// counts these events, so filtering them would only put the badge at odds
	// with the pill.
	const orderedEvents = useMemo(() => sortBySequence(events ?? []), [events]);

	// The standalone Evidence list is gone. It showed everything the model
	// touched, up front, whether or not the reader cared — and it competed with
	// the timeline for the same column. What replaces it is the reader's own
	// chain: citations they clicked in the answer, in the order they clicked
	// them (``CitationChainPanel``).
	//
	// With the flag OFF no provider is mounted, and the standalone evidence
	// list is still the right surface — nothing else would show evidence at
	// all. The legacy path is kept whole rather than half-migrated, so turning
	// the flag off is a true revert.
	const citationChain = useCitationChain();
	const evidenceItemRefs = useRef<Map<string, HTMLLIElement>>(new Map());
	const handleUpstreamClick = useCallback((eventId: string) => {
		const node = evidenceItemRefs.current.get(eventId);
		if (!node) return;
		node.scrollIntoView({ behavior: "smooth", block: "center" });
		node.classList.add("trace-evidence-item--highlight");
		window.setTimeout(() => {
			node.classList.remove("trace-evidence-item--highlight");
		}, 1200);
	}, []);
	const setEvidenceItemRef = useCallback(
		(eventId: string) => (node: HTMLLIElement | null) => {
			if (node) evidenceItemRefs.current.set(eventId, node);
			else evidenceItemRefs.current.delete(eventId);
		},
		[],
	);
	const evidenceItemLabel = `${orderedEvents.length} ${orderedEvents.length === 1 ? "item" : "items"}`;

	const timelineItems = useMemo(
		() =>
			useTimeline
				? buildAuditTimeline(contentBlocks ?? [], { documentRefs, chatType })
				: [],
		[useTimeline, contentBlocks, documentRefs, chatType],
	);

	const inspectionSteps = inspections ?? [];
	// Auto-scroll the panel to the newest inspection whenever one is appended,
	// so the latest column lineage stays visible as the list overflows.
	const inspectionEndRef = useRef<HTMLDivElement>(null);
	const prevInspectionCount = useRef(0);
	useEffect(() => {
		if (
			isOpen &&
			inspectionSteps.length > prevInspectionCount.current &&
			inspectionEndRef.current
		) {
			inspectionEndRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
		prevInspectionCount.current = inspectionSteps.length;
	}, [inspectionSteps.length, isOpen]);
	// With the timeline on, the badge is the SAME string the collapsed pill
	// shows — both call `auditLogCounts`/`formatAuditLogCounts` over the same
	// message. A drawer headed "1 step" behind a pill reading "3 steps · 22
	// evidence" is the defect this replaces (DOP-415).
	//
	// The legacy path is untouched: with the chain on, the badge counts STEPS
	// only (the chain is the reader's path, not the model's work, so counting
	// its cards would make the header drift as they browse); with it off,
	// evidence is a fixed list and keeps contributing, as it always did.
	// Column-lineage inspections render as steps either way, so they count in
	// both.
	const legacyStepCount = useMemo(
		() =>
			useTimeline ? 0 : blocks.filter((b) => renderTraceBlock(b) !== null).length,
		[useTimeline, blocks],
	);
	const stepBadgeLabel = useMemo(() => {
		if (useTimeline) {
			return formatAuditLogCounts(
				auditLogCounts(contentBlocks ?? [], {
					documentRefs,
					chatType,
					evidenceCount: orderedEvents.length,
					inspectionCount: inspectionSteps.length,
				}),
			);
		}
		const count =
			(citationChain ? legacyStepCount : legacyStepCount + orderedEvents.length) +
			inspectionSteps.length;
		return `${count} ${count === 1 ? "step" : "steps"}`;
	}, [
		useTimeline,
		contentBlocks,
		documentRefs,
		chatType,
		orderedEvents.length,
		inspectionSteps.length,
		citationChain,
		legacyStepCount,
	]);
	// Embedded, the host owns the width: no collapse animation to run, no
	// handle to drag, and no inline width to fight the host's flex box.
	const wrapperClassName = isEmbedded
		? "trace-panel-embedded"
		: [
				"trace-panel-wrapper",
				isOpen ? "trace-panel-open" : "",
				isResizing ? "trace-panel-resizing" : "",
			]
				.filter(Boolean)
				.join(" ");
	// Dynamic, user-controlled width only applies while open; closed state keeps
	// the CSS width:0 collapse.
	const wrapperStyle = !isEmbedded && isOpen ? { width, minWidth: width } : undefined;
	const isEmpty =
		(useTimeline ? timelineItems.length === 0 : blocks.length === 0) &&
		inspectionSteps.length === 0 &&
		(citationChain
			? citationChain.chain.length === 0
			: orderedEvents.length === 0);
	const emptyStateLabel = isStreaming
		? "Waiting for trace events..."
		: "No trace data available.";

	const handleCopy = useCallback(() => {
		navigator.clipboard
			.writeText(JSON.stringify(blocks, null, 2))
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			});
	}, [blocks]);

	return (
		<div className={wrapperClassName} style={wrapperStyle}>
			<div className="trace-panel">
				{!isEmbedded && isOpen && (
					<ResizeHandle
						edge="left"
						isResizing={isResizing}
						onPointerDown={startResize}
						label="Resize audit log panel"
					/>
				)}
				<header className="trace-panel-header">
					<div className="trace-panel-header-left">
						{isEmbedded ? (
							// The rail has one body and this log is currently it, so
							// the way out is a way BACK to the thread, not a second
							// panel to dismiss.
							<button
								type="button"
								className="trace-panel-back"
								onClick={onClose}
							>
								<span aria-hidden="true">&lsaquo;</span> Thread
							</button>
						) : (
							<img src={tickIcon} alt="" className="trace-panel-tick" />
						)}
						<span className="trace-panel-title">Audit Log</span>
						<span className="trace-panel-badge">{stepBadgeLabel}</span>
						<button
							type="button"
							className="trace-panel-icon-btn"
							onClick={handleCopy}
							title="Copy trace log"
							aria-label="Copy trace log"
						>
							{copied ? (
								<Check className="trace-panel-icon-btn-icon" />
							) : (
								<Copy className="trace-panel-icon-btn-icon" />
							)}
						</button>
						{isStreaming && (
							<span
								className="trace-panel-streaming-dot"
								aria-label="streaming"
							/>
						)}
					</div>
					<button
						type="button"
						className="trace-panel-close"
						onClick={onClose}
						title="Close trace panel"
						aria-label="Close trace panel"
					>
						<X className="trace-panel-close-icon" />
					</button>
				</header>
				{isEmbedded && aboutLabel && (
					<p className="trace-panel-about">
						For the answer to &ldquo;{aboutLabel}&rdquo;
					</p>
				)}

				<div className="trace-panel-body">
					{isEmpty && (
						<div className="trace-panel-empty">{emptyStateLabel}</div>
					)}
					{useTimeline ? (
						<AuditTimeline items={timelineItems} />
					) : (
						blocks.map((block, index) => (
							<TraceStepItem key={index} block={block} />
						))
					)}
					{citationChain ? (
						<CitationChainPanel
							chain={citationChain.chain}
							allEvents={orderedEvents}
							chatId={chatId}
							focusedKey={citationChain.focusedKey}
							onDismiss={citationChain.dismiss}
						/>
					) : (
						// The timeline drawer lists steps only: the answer's inline
						// citations already carry the sources, so repeating them
						// here as a list is noise (operator decision, DOP-415).
						!useTimeline &&
						orderedEvents.length > 0 && (
							<section className="trace-evidence-section">
								<div className="trace-evidence-heading">
									Evidence
									<span className="trace-evidence-count">
										{evidenceItemLabel}
									</span>
								</div>
								<ol className="trace-evidence-list">
									{orderedEvents.map((event) => (
										<li
											key={event.event_id}
											ref={setEvidenceItemRef(event.event_id)}
											className="trace-step trace-evidence-item"
											data-event-id={event.event_id}
											data-event-kind={event.kind}
										>
											<span className="trace-step-bullet" aria-hidden="true" />
											<div className="trace-step-content">
												{renderEvidenceEvent({
													event,
													allEvents: orderedEvents,
													chatId,
													onUpstreamClick: handleUpstreamClick,
												})}
											</div>
										</li>
									))}
								</ol>
							</section>
						)
					)}
					{inspectionSteps.length > 0 && (
						<section className="trace-inspection-section">
							<div className="trace-inspection-heading">
								Column lineage
								<span className="trace-inspection-count">
									{inspectionSteps.length}{" "}
									{inspectionSteps.length === 1 ? "click" : "clicks"}
								</span>
							</div>
							{inspectionSteps.map((inspection) => (
								<InspectionStepItem
									key={inspection.id}
									inspection={inspection}
								/>
							))}
							<div ref={inspectionEndRef} aria-hidden="true" />
						</section>
					)}
				</div>
			</div>
		</div>
	);
};

export default TracePanel;
